import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import useAnchoredPanel from '../components/common/useAnchoredPanel';
import PageHeader from '../components/common/PageHeader';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import { useLiveReload } from '../components/common/useLiveReload';
import { withReturnTo } from '../components/common/returnPath';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useInfiniteScroll, LoadingMore, EndOfList } from '../hooks/useInfiniteScroll';
import { isNestedSiteType, previewPath } from '../components/common/pathRules';
import { searchStateBadge } from '../components/sites/searchState';
import type {
  MarkReadyBlocked,
  MarkReadyResponse,
  ManagedPage,
  ManagedSite,
  DeploymentPlan,
  Topic,
  Category,
  Project,
  PageBuildStatus,
  PageStyle,
  WebSocketEvent,
} from '../types';

const PAGE_SIZE = 25;

const STATUS_OPTIONS: { value: PageBuildStatus | ''; label: string }[] = [
  { value: '', label: 'All Statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'QUEUED', label: 'Queued' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'WAITING_FOR_USER_ACTION', label: 'Waiting for User' },
  { value: 'WAITING_FOR_SYSTEM_ACTION', label: 'Waiting for System' },
  { value: 'READY_TO_DEPLOY', label: 'Ready to Deploy' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'CANCELLED', label: 'Canceled' },
  { value: 'SKIPPED', label: 'Skipped' },
  { value: 'ERROR', label: 'Error' },
];

const PAGE_STYLES: { value: PageStyle | ''; label: string }[] = [
  { value: '', label: 'All Styles' },
  { value: 'ARTICLE', label: 'Article' },
  { value: 'GUIDE', label: 'Guide' },
  { value: 'CHECKLIST', label: 'Checklist' },
  { value: 'LISTICLE', label: 'Listicle' },
  { value: 'FAQ', label: 'FAQ' },
  { value: 'COMPARISON', label: 'Comparison' },
  { value: 'REVIEWS', label: 'Reviews' },
  { value: 'PRODUCT_PAGE', label: 'Product Page' },
  { value: 'PRICING_PAGE', label: 'Pricing Page' },
  { value: 'SUPPORT_ARTICLE', label: 'Support Article' },
  { value: 'DOCUMENTATION', label: 'Documentation' },
  { value: 'TOOL', label: 'Tool' },
  { value: 'FORUM_DISCUSSION', label: 'Forum Discussion' },
  { value: 'VIDEO', label: 'Video' },
  { value: 'TEMPLATE', label: 'Template' },
];

function getStatusColor(status: PageBuildStatus | null): string {
  switch (status) {
    case 'COMPLETED': return 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300';
    case 'IN_PROGRESS': return 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300';
    case 'WAITING_FOR_USER_ACTION': return 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300';
    case 'WAITING_FOR_SYSTEM_ACTION': return 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300';
    case 'FAILED':
    case 'ERROR': return 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300';
    case 'QUEUED': return 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300';
    case 'PENDING': return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
    case 'READY_TO_DEPLOY': return 'bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300';
    case 'DRAFT':
    case 'CANCELLED':
    case 'SKIPPED': return 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400';
    default: return 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400';
  }
}

/** Matches the w-64 this menu has always been. */
const PLAN_MENU_WIDTH = 256;

export default function TopicPages() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { subscribe } = useWebSocket();

  const topicId = searchParams.get('topic') ? parseInt(searchParams.get('topic')!, 10) : null;
  const categoryId = searchParams.get('category') ? parseInt(searchParams.get('category')!, 10) : null;
  const projectId = searchParams.get('project') ? parseInt(searchParams.get('project')!, 10) : null;
  const keywordParam = searchParams.get('keyword') || '';
  const statusParam = searchParams.get('status') || '';
  // Deep-link from Settings ▸ Deployment Targets ("View pages →") so a plan
  // opens pre-filtered. Site comes along because the plan dropdown is populated
  // per-site — without it the plan filter would have nothing to resolve against.
  const siteParam = searchParams.get('site') ? parseInt(searchParams.get('site')!, 10) : '';
  const planParam = searchParams.get('plan') ? parseInt(searchParams.get('plan')!, 10) : '';

  // Context info for breadcrumb
  const [topic, setTopic] = useState<Topic | null>(null);
  const [category, setCategory] = useState<Category | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [, setLoadingContext] = useState(true);

  // Sites and plans for filters
  const [sites, setSites] = useState<ManagedSite[]>([]);
  const [plans, setPlans] = useState<DeploymentPlan[]>([]);
  const [loadingSites, setLoadingSites] = useState(true);

  // Pages list
  const [pages, setPages] = useState<ManagedPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalItems, setTotalItems] = useState(0);

  // Filters - initialize search with keyword from URL, status from URL
  const [search, setSearch] = useState(keywordParam);
  const [statusFilter, setStatusFilter] = useState<PageBuildStatus | ''>(
    (statusParam as PageBuildStatus) || ''
  );
  const [styleFilter, setStyleFilter] = useState<PageStyle | ''>('');
  const [siteFilter, setSiteFilter] = useState<number | ''>(siteParam);
  const [planFilter, setPlanFilter] = useState<number | ''>(planParam);
  // WHERE IT CAME FROM and WHERE IT IS FILED — two different questions, and
  // since a page carries its own publish path they can have different answers.
  // Site says which target; Path says where on it.
  const [subjectFilter, setSubjectFilter] = useState<number | ''>('');
  const [pathFilter, setPathFilter] = useState<string>('');
  const [subjects, setSubjects] = useState<Project[]>([]);
  const [pathOptions, setPathOptions] = useState<{ segment: string; page_count: number }[]>([]);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isInitialLoad = useRef(true);

  // Fetch sites on mount
  useEffect(() => {
    const fetchSites = async () => {
      setLoadingSites(true);
      try {
        const data = await api.getManagedSites();
        setSites(data);
      } catch (err) {
        console.error('Failed to load sites:', err);
      } finally {
        setLoadingSites(false);
      }
    };
    fetchSites();
  }, []);

  // Subjects, for the "where it came from" filter.
  useEffect(() => {
    api
      .getProjects()
      .then(setSubjects)
      .catch((err) => console.error('Failed to load subjects:', err));
  }, []);

  // Path options come from the SITE, because a path only means anything on one
  // — two subjects publishing to the same site share its structure. Without a
  // site chosen there is nothing coherent to offer, so the control says so
  // rather than listing every path across every site as if they were one tree.
  useEffect(() => {
    if (!siteFilter) {
      setPathOptions([]);
      setPathFilter('');
      return;
    }
    let cancelled = false;
    api
      .getPathSegments(Number(siteFilter), pathFilter ? pathFilter.split('/') : [])
      .then((res) => {
        if (!cancelled) setPathOptions(res.suggestions);
      })
      .catch(() => {
        if (!cancelled) setPathOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [siteFilter, pathFilter]);

  // Fetch plans when site changes
  useEffect(() => {
    const fetchPlans = async () => {
      if (!siteFilter) {
        setPlans([]);
        setPlanFilter('');
        return;
      }
      try {
        const data = await api.getDeploymentPlans({ managedSiteId: siteFilter, size: 100 });
        setPlans(data.items);
        // Reset plan filter if current plan doesn't belong to selected site
        if (planFilter && !data.items.find(p => p.id === planFilter)) {
          setPlanFilter('');
        }
      } catch (err) {
        console.error('Failed to load plans:', err);
        setPlans([]);
      }
    };
    fetchPlans();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteFilter]);

  // Fetch context (project, category, topic names)
  useEffect(() => {
    const fetchContext = async () => {
      setLoadingContext(true);
      try {
        if (topicId) {
          const topicData = await api.getTopic(topicId);
          setTopic(topicData);
        }
        if (categoryId) {
          const categoryData = await api.getCategory(categoryId);
          setCategory(categoryData);
        }
        if (projectId) {
          const projectData = await api.getProject(projectId);
          setProject(projectData);
        }
      } catch (err) {
        console.error('Failed to load context:', err);
      } finally {
        setLoadingContext(false);
      }
    };
    fetchContext();
  }, [topicId, categoryId, projectId]);

  const fetchPages = useCallback(async (pageNum: number, append: boolean = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setPages([]);
      setHasMore(false);
      isInitialLoad.current = true;
    }

    try {
      const data = await api.getManagedPages({
        topicId: topicId || undefined,
        status: statusFilter || undefined,
        pageStyle: styleFilter || undefined,
        managedSiteId: siteFilter || undefined,
        deploymentPlanId: planFilter || undefined,
        projectId: subjectFilter || undefined,
        path: pathFilter || undefined,
        search: search || undefined,
        page: pageNum,
        size: PAGE_SIZE,
      });

      if (append) {
        setPages(prev => {
          const existingIds = new Set(prev.map(p => p.id));
          const newItems = data.items.filter(p => !existingIds.has(p.id));
          return [...prev, ...newItems];
        });
      } else {
        setPages(data.items);
      }

      setTotalItems(data.total);
      setHasMore(pageNum < data.pages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pages');
    } finally {
      setLoading(false);
      setLoadingMore(false);
      isInitialLoad.current = false;
    }
  }, [topicId, statusFilter, styleFilter, siteFilter, planFilter, subjectFilter, pathFilter, search]);

  // Initial load and filter changes
  useEffect(() => {
    setPage(1);
    scrollContainerRef.current?.scrollTo(0, 0);
    fetchPages(1, false);
  }, [topicId, statusFilter, styleFilter, siteFilter, planFilter, subjectFilter, pathFilter, search, fetchPages]);

  const loadMore = useCallback(() => {
    if (isInitialLoad.current) return;
    if (!loadingMore && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchPages(nextPage, true);
    }
  }, [loadingMore, hasMore, page, fetchPages]);

  useInfiniteScroll({
    onLoadMore: loadMore,
    hasMore,
    isLoading: loading || loadingMore,
  });

  // Live status badges: when a `page_build` event arrives for a page
  // currently shown in this list, its DB status changed — refetch the
  // first page so the badge re-renders. `event.status` is a websocket
  // lifecycle value (not the DB enum), so we refetch rather than patch.
  useEffect(() => {
    return subscribe((event: WebSocketEvent) => {
      if (event.event_type !== 'page_build') return;
      if (pages.some((p) => p.id === event.page_id)) {
        setPage(1);
        fetchPages(1, false);
      }
    });
  }, [subscribe, pages, fetchPages]);
  // NEW PAGES TOO. The check above only hears pages already listed, so a page
  // a bulk build or a growth session created never joined the list. A build
  // starting on a page we do not have, or a session ending, re-reads it.
  useLiveReload(
    (e) =>
      (e?.event_type === 'page_build' && e.status === 'started' && !pages.some((p) => p.id === e.page_id)) ||
      (e?.event_type === 'growth_agent_llm' && e.status === 'session_complete'),
    () => {
      setPage(1);
      fetchPages(1, false);
    },
    3_000,
  );

  // ── Bulk selection ────────────────────────────────────────────────────
  //
  // The list is infinite-scrolled, so `selectedIds` only ever holds rows that
  // have actually been rendered. `allMatchingSelected` is the escape hatch for
  // "every page matching the current filters", which can be far more than is on
  // screen — its ids are fetched lazily at action time (fetchAllMatchingIds)
  // rather than held in state, so selecting 50k pages costs nothing until you
  // act on them.
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [allMatchingSelected, setAllMatchingSelected] = useState(false);
  const [bulkBusy, setBulkBusy] = useState<'queue' | 'ready' | 'plan' | 'delete' | null>(null);
  const [showPlanDropdown, setShowPlanDropdown] = useState(false);
  const planBtnRef = useRef<HTMLButtonElement>(null);
  const planPos = useAnchoredPanel(showPlanDropdown, planBtnRef, PLAN_MENU_WIDTH);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setAllMatchingSelected(false);
    setShowPlanDropdown(false);
  }, []);

  // Any filter change invalidates a selection built under the old filters —
  // acting on it afterwards would hit rows the user can no longer see.
  useEffect(() => {
    clearSelection();
  }, [topicId, statusFilter, styleFilter, siteFilter, planFilter, search, clearSelection]);

  const togglePage = (id: number) => {
    setAllMatchingSelected(false);
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const loadedAllSelected = pages.length > 0 && selectedIds.size === pages.length;

  const toggleSelectAllLoaded = () => {
    if (loadedAllSelected) {
      clearSelection();
    } else {
      setAllMatchingSelected(false);
      setSelectedIds(new Set(pages.map(p => p.id)));
    }
  };

  /** Every id matching the current filters, walked a page at a time. */
  const fetchAllMatchingIds = async (): Promise<number[]> => {
    const ids: number[] = [];
    let pageNum = 1;
    // Bounded so a runaway total can't spin forever. 100 is the server's cap
    // (list_managed_pages declares size le=100) — asking for more 422s the
    // whole request, which would take "select all matching" with it.
    for (let guard = 0; guard < 500; guard++) {
      const data = await api.getManagedPages({
        topicId: topicId || undefined,
        status: statusFilter || undefined,
        pageStyle: styleFilter || undefined,
        managedSiteId: siteFilter || undefined,
        deploymentPlanId: planFilter || undefined,
        search: search || undefined,
        page: pageNum,
        size: 100,
      });
      ids.push(...data.items.map(p => p.id));
      if (pageNum >= data.pages || data.items.length === 0) break;
      pageNum++;
    }
    return ids;
  };

  const targetIds = async () =>
    allMatchingSelected ? await fetchAllMatchingIds() : Array.from(selectedIds);

  const selectionCount = allMatchingSelected ? totalItems : selectedIds.size;

  // The outcome of a mark-ready run. Kept in state rather than alert()ed because
  // the blocked list is the useful half and an alert cannot show it: 200 pages
  // stopped by one missing topic slug is ONE fix, and only grouping shows that.
  const [markReadyResult, setMarkReadyResult] = useState<MarkReadyResponse | null>(null);

  const handleMarkReady = async () => {
    setBulkBusy('ready');
    setMarkReadyResult(null);
    try {
      const ids = await targetIds();
      const result = await api.bulkMarkPagesReady(ids);
      setMarkReadyResult(result);
      if (result.marked_count > 0) {
        clearSelection();
        setPage(1);
        fetchPages(1, false);
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to mark pages ready');
    } finally {
      setBulkBusy(null);
    }
  };

  const handleQueueForBuild = async () => {
    if (!window.confirm(
      `Queue ${selectionCount.toLocaleString()} page(s) for building?\n\n` +
      `They'll be picked up by the build pipeline. Pages already building are left alone.`
    )) return;
    setBulkBusy('queue');
    try {
      const ids = await targetIds();
      const result = await api.bulkSetManagedPageStatus(ids, 'WAITING_FOR_SYSTEM_ACTION');
      // Report the server's numbers, not the request size — it may decline
      // pages that were mid-build, and a silent shortfall reads as success.
      const skipped = result.skipped_count ?? 0;
      alert(
        `Queued ${result.updated_count.toLocaleString()} page(s).` +
        (skipped ? ` ${skipped.toLocaleString()} skipped (${result.skipped_reason ?? 'not eligible'}).` : '')
      );
      clearSelection();
      setPage(1);
      fetchPages(1, false);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to queue pages');
    } finally {
      setBulkBusy(null);
    }
  };

  const handleBulkAssignPlan = async (newPlanId: number | null) => {
    const target = plans.find(p => p.id === newPlanId);
    if (!window.confirm(
      newPlanId
        ? `Move ${selectionCount.toLocaleString()} page(s) to "${target?.name}"?`
        : `Remove ${selectionCount.toLocaleString()} page(s) from their plan?`
    )) return;
    setBulkBusy('plan');
    setShowPlanDropdown(false);
    try {
      const ids = await targetIds();
      await api.bulkUpdateManagedPages(ids, newPlanId);
      clearSelection();
      setPage(1);
      fetchPages(1, false);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to move pages');
    } finally {
      setBulkBusy(null);
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(
      `Delete ${selectionCount.toLocaleString()} page(s)? This cannot be undone.`
    )) return;
    setBulkBusy('delete');
    try {
      const ids = await targetIds();
      // One request, like the other two bulk actions. Deleting a selection a
      // page at a time is N round trips that abort half-applied on the first
      // failure — and this selection can be every page matching the filters.
      const result = await api.bulkDeleteManagedPages(ids);
      const skipped = result.skipped_count ?? 0;
      if (skipped) {
        alert(
          `Deleted ${result.updated_count.toLocaleString()} page(s). ` +
          `${skipped.toLocaleString()} skipped (${result.skipped_reason ?? 'not eligible'}).`
        );
      }
      clearSelection();
      setPage(1);
      fetchPages(1, false);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete pages');
    } finally {
      setBulkBusy(null);
    }
  };

  const handleEditPage = (pageItem: ManagedPage) => {
    // With the trail: the builder's own breadcrumb offers a bare `/pages`,
    // which happens to be this screen but arrives with every filter, the page
    // number and the selection thrown away.
    navigate(
      withReturnTo(
        `/page-builder/${pageItem.id}`,
        `${location.pathname}${location.search}`,
        'Pages',
      ),
    );
  };

  // Deployment Targets lives at Settings ▸ Page Generation ▸ Deployment Targets.
  const handleSiteClick = (e: React.MouseEvent, siteId: number | null) => {
    e.stopPropagation();
    if (siteId) {
      navigate(`/deliver?site=${siteId}`);
    }
  };

  const handlePlanClick = (e: React.MouseEvent, siteId: number | null, planId: number | null) => {
    e.stopPropagation();
    if (siteId && planId) {
      navigate(`/deliver?site=${siteId}&plan=${planId}`);
    }
  };

  /**
   * Where to READ this page as the site it deploys to will publish it, or null
   * when there is no such site.
   *
   * The row already says which plan a page is in; it could not say what the page
   * would look like once that plan ran. The designer's preview compiles the same
   * shell the Hugo layouts come from, so pointing it at this page's public path
   * is the closest thing to opening the published page early. `previewPath` is
   * the same rule the path editor shows, so the URL here and the URL promised
   * there cannot disagree.
   */
  const previewHref = (pageItem: ManagedPage): string | null => {
    if (!pageItem.managed_site_id || !pageItem.deployment_plan_id) return null;
    if (!isNestedSiteType(pageItem.managed_site_type)) return null;
    const path = previewPath(pageItem.path_segments ?? [], pageItem.slug ?? '', true);
    if (path === '/') return null;
    // Carry the trail: the preview's own exit is the site designer, a screen
    // nobody following this link asked for. With a `from` it comes back here,
    // filters and page intact.
    return withReturnTo(
      `/deliver/${pageItem.managed_site_id}/design/preview` +
        `?plan=${pageItem.deployment_plan_id}&ready_only=1&path=${encodeURIComponent(path)}`,
      `${location.pathname}${location.search}`,
      'Pages',
    );
  };

  const clearFilters = () => {
    setStatusFilter('');
    setStyleFilter('');
    setSiteFilter('');
    setPlanFilter('');
    setSubjectFilter('');
    setPathFilter('');
    setSearch('');
  };

  const searchHasValue = search.trim().length > 0;
  const hasFilters =
    statusFilter || styleFilter || siteFilter || planFilter || subjectFilter || pathFilter || search;

  return (
    <div>
      <PageHeader
        title={topic ? `Pages for ${topic.name}` : 'Pages'}
        subtitle={
          <>
            {totalItems} page{totalItems !== 1 ? 's' : ''}
            {statusFilter && ` - filtered by ${statusFilter.replace(/_/g, ' ').toLowerCase()}`}
          </>
        }
        breadcrumb={[
          ...(topicId
            ? [
                { label: 'Subjects', to: '/subjects' },
                ...(project
                  ? [{ label: project.name, to: `/subjects/${projectId}` }]
                  : []),
                ...(category
                  ? [{ label: category.name, to: `/subjects/${projectId}?node=${categoryId}` }]
                  : []),
                ...(topic
                  ? [{ label: topic.name, to: `/subjects/${projectId}?node=${topicId}` }]
                  : []),
              ]
            : // "Home" is what the nav and the page itself both call it; this
              // said "Dashboard", a name the app stopped using.
              [{ label: 'Home', to: '/' }]),
          { label: 'Pages', isCurrent: true },
        ]}
      />

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 border rounded-lg p-4 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Search</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Keyword or title..."
              className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none transition-shadow ${
                searchHasValue
                  ? 'border-red-300 dark:border-red-700 ring-1 ring-red-200 focus:ring-red-400 focus:border-red-400 dark:focus:border-red-700 shadow-[0_0_0_3px_rgba(248,113,113,0.25)]'
                  : 'border-gray-300 dark:border-gray-600 focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
              }`}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as PageBuildStatus | '')}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Style</label>
            <select
              value={styleFilter}
              onChange={(e) => setStyleFilter(e.target.value as PageStyle | '')}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              {PAGE_STYLES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Site</label>
            <select
              value={siteFilter}
              onChange={(e) => setSiteFilter(e.target.value ? Number(e.target.value) : '')}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              disabled={loadingSites}
            >
              <option value="">All Sites</option>
              {sites.map((site) => (
                <option key={site.id} value={site.id}>{site.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Plan</label>
            <select
              value={planFilter}
              onChange={(e) => setPlanFilter(e.target.value ? Number(e.target.value) : '')}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              disabled={!siteFilter}
            >
              <option value="">{siteFilter ? 'All Plans' : 'Select a site first'}</option>
              {plans.map((plan) => (
                <option key={plan.id} value={plan.id}>{plan.name}</option>
              ))}
            </select>
          </div>
          <div>
            {/* WHERE IT CAME FROM. Distinct from Site, which is where it goes —
                one subject can publish to several sites and one site can carry
                several subjects, so neither answers for the other. */}
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Subject</label>
            <select
              value={subjectFilter}
              onChange={(e) => setSubjectFilter(e.target.value ? Number(e.target.value) : '')}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">All Subjects</option>
              {subjects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div>
            {/* WHERE IT IS FILED on that site. One level at a time: picking a
                segment narrows the list and the next call offers what is under
                it, so a deep path is walked rather than typed. */}
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Path</label>
            <select
              value=""
              onChange={(e) => {
                const v = e.target.value;
                if (!v) return;
                setPathFilter(pathFilter ? `${pathFilter}/${v}` : v);
              }}
              disabled={!siteFilter || pathOptions.length === 0}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono disabled:bg-gray-50 dark:disabled:bg-gray-900"
            >
              <option value="">
                {!siteFilter
                  ? 'Select a site first'
                  : pathOptions.length === 0
                    ? pathFilter
                      ? 'Nothing deeper'
                      : 'No paths on this site'
                    : 'Narrow by path…'}
              </option>
              {pathOptions.map((o) => (
                <option key={o.segment} value={o.segment}>
                  {o.segment} ({o.page_count})
                </option>
              ))}
            </select>
            {pathFilter && (
              <div className="mt-1 flex items-center gap-1 text-xs">
                <code className="text-gray-600 dark:text-gray-300 truncate">/{pathFilter}/</code>
                <button
                  type="button"
                  onClick={() => setPathFilter('')}
                  className="icon-btn text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 shrink-0"
                  aria-label="Clear path filter"
                >
                  ×
                </button>
              </div>
            )}
          </div>
        </div>
        {hasFilters && (
          <div className="mt-3 flex justify-end">
            <button
              onClick={clearFilters}
              className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              Clear filters
            </button>
          </div>
        )}
      </div>

      {/* Pages List */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Bulk action bar — only present when something is selected, so the
          table reads normally the rest of the time. */}
      {selectionCount > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-2.5 mb-3 flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-blue-900 dark:text-blue-200">
            {allMatchingSelected
              ? `All ${totalItems.toLocaleString()} matching`
              : `${selectedIds.size.toLocaleString()} selected`}
          </span>
          {/* Offered once the loaded rows are all ticked and more exist behind
              the infinite scroll — otherwise "select all" would silently mean
              "all 25 I happen to have fetched". */}
          {loadedAllSelected && !allMatchingSelected && totalItems > pages.length && (
            <button
              onClick={() => setAllMatchingSelected(true)}
              className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
            >
              Select all {totalItems.toLocaleString()} matching
            </button>
          )}
          <div className="flex-1" />
          <button
            onClick={handleQueueForBuild}
            disabled={bulkBusy !== null}
            title="Set status to 'Waiting for System' so the build pipeline picks these up"
            className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
          >
            {bulkBusy === 'queue' ? 'Queuing...' : 'Queue for build'}
          </button>
          <button
            onClick={handleMarkReady}
            disabled={bulkBusy !== null}
            title="Check these against the deploy rules and mark the ones that pass as Ready to deploy"
            className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
          >
            {bulkBusy === 'ready' ? 'Checking...' : 'Mark ready to deploy'}
          </button>
          <div className="relative">
            <button
              ref={planBtnRef}
              onClick={() => setShowPlanDropdown(v => !v)}
              disabled={bulkBusy !== null || !siteFilter}
              title={siteFilter ? 'Move to another deployment plan' : 'Pick a site first to list its plans'}
              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {bulkBusy === 'plan' ? 'Moving...' : 'Change plan'}
            </button>
            {showPlanDropdown &&
              planPos &&
              createPortal(
              <>
              {/* click-away — without it the menu hangs around behind the next thing you click */}
              <div className="fixed inset-0 z-[100]" onClick={() => setShowPlanDropdown(false)} />
              {/* Portalled: this bulk bar sits in a scrolling table header, which
                  clipped the menu at the header's edge. See common/anchoredPanel.ts. */}
              <div
                style={{
                  top: planPos.top,
                  bottom: planPos.bottom,
                  left: planPos.left,
                  width: PLAN_MENU_WIDTH,
                  maxHeight: planPos.maxHeight,
                }}
                className="fixed z-[101] overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg">
                <div className="py-1">
                  <button
                    onClick={() => handleBulkAssignPlan(null)}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    Remove from plan
                  </button>
                  <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                  <div className="max-h-48 overflow-y-auto">
                    {plans.map((plan) => (
                      <button
                        key={plan.id}
                        onClick={() => handleBulkAssignPlan(plan.id)}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        {plan.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              </>,
              document.body,
            )}
          </div>
          <button
            onClick={handleBulkDelete}
            disabled={bulkBusy !== null}
            className="px-3 py-1.5 text-sm border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 rounded-md hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50"
          >
            {bulkBusy === 'delete' ? 'Deleting...' : 'Delete'}
          </button>
          <button
            onClick={clearSelection}
            disabled={bulkBusy !== null}
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-50"
          >
            Clear
          </button>
        </div>
      )}

      {markReadyResult && (
        <MarkReadyReport
          result={markReadyResult}
          onDismiss={() => setMarkReadyResult(null)}
        />
      )}

      {loading && pages.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">Loading pages...</div>
      ) : pages.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800">
          {hasFilters
            ? 'No pages match your filters.'
            : 'No managed pages for this topic yet.'}
        </div>
      ) : (
        <div
          ref={scrollContainerRef}
          className="bg-white dark:bg-gray-800 border rounded-lg overflow-hidden max-h-[calc(100vh-400px)] overflow-y-auto"
        >
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-800 border-b sticky top-0">
              <tr>
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={loadedAllSelected}
                    onChange={toggleSelectAllLoaded}
                    aria-label="Select all loaded pages"
                    className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Keyword</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Title</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Style</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Site</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Plan</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {pages.map((pageItem) => (
                <tr
                  key={pageItem.id}
                  onClick={() => handleEditPage(pageItem)}
                  className={`hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer ${
                    selectedIds.has(pageItem.id) || allMatchingSelected
                      ? 'bg-blue-50 dark:bg-blue-900/20'
                      : ''
                  }`}
                >
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={allMatchingSelected || selectedIds.has(pageItem.id)}
                      onChange={() => togglePage(pageItem.id)}
                      aria-label={`Select ${pageItem.keyword_value}`}
                      className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300">
                      {pageItem.keyword_value}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600 dark:text-gray-300 truncate max-w-[200px] block" title={pageItem.title || ''}>
                      {pageItem.title ? (pageItem.title.length > 40 ? pageItem.title.slice(0, 40) + '...' : pageItem.title) : '-'}
                    </span>
                    {previewHref(pageItem) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(previewHref(pageItem) as string);
                        }}
                        className="text-[11px] text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 hover:underline"
                        title="Read this page in the site it will deploy into"
                      >
                        preview ↗
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 text-xs font-medium bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 rounded">
                      {PAGE_STYLES.find(s => s.value === pageItem.page_style)?.label || pageItem.page_style}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded ${getStatusColor(pageItem.status)}`}>
                      {STATUS_OPTIONS.find(s => s.value === pageItem.status)?.label || pageItem.status || 'No Status'}
                    </span>
                    {(() => {
                      const badge = searchStateBadge(pageItem.search_state);
                      return badge ? (
                        <span
                          className={`ml-1 px-2 py-0.5 text-xs font-medium rounded ${badge.className}`}
                          title="From Search Console - see the site's Search tab"
                        >
                          {badge.label}
                        </span>
                      ) : null;
                    })()}
                  </td>
                  <td className="px-4 py-3">
                    {pageItem.managed_site_name ? (
                      <button
                        onClick={(e) => handleSiteClick(e, pageItem.managed_site_id)}
                        className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline"
                      >
                        {pageItem.managed_site_name}
                      </button>
                    ) : (
                      <span className="text-sm text-gray-400 dark:text-gray-500">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {pageItem.deployment_plan_name ? (
                      <button
                        onClick={(e) => handlePlanClick(e, pageItem.managed_site_id, pageItem.deployment_plan_id)}
                        className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline"
                      >
                        {pageItem.deployment_plan_name}
                      </button>
                    ) : (
                      <span className="text-sm text-gray-400 dark:text-gray-500">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <LoadingMore isLoading={loadingMore} />
          <EndOfList show={!hasMore && !loading} itemCount={pages.length} />
        </div>
      )}
    </div>
  );
}

/**
 * What a mark-ready run did, and — the useful half — what it refused.
 *
 * Blocked pages are GROUPED BY REASON because that is the shape of the fix: two
 * hundred pages stopped by "Topic 'Boats' has no slug" is one missing slug, not
 * two hundred problems, and a flat list of two hundred rows hides that
 * completely. The old flow never showed this at all — a page was marked ready
 * by a dropdown that asked nothing, and the first anyone heard of a problem was
 * at deploy.
 */
function MarkReadyReport({
  result,
  onDismiss,
}: {
  result: MarkReadyResponse;
  onDismiss: () => void;
}) {
  // A plain record rather than a Map: this tsconfig targets es5, where
  // iterating a Map needs --downlevelIteration.
  const groups: Record<string, MarkReadyBlocked[]> = {};
  for (const b of result.blocked) {
    (groups[b.reason] ||= []).push(b);
  }
  const ordered = Object.entries(groups).sort((a, b) => b[1].length - a[1].length);

  return (
    <div className="mb-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {result.marked_count > 0
              ? `${result.marked_count.toLocaleString()} page${result.marked_count === 1 ? '' : 's'} marked ready to deploy`
              : 'Nothing was marked ready'}
            {result.already_ready_count > 0 &&
              ` · ${result.already_ready_count.toLocaleString()} already ready`}
          </p>
          {result.blocked.length > 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {result.blocked.length.toLocaleString()} page
              {result.blocked.length === 1 ? '' : 's'} can’t deploy yet:
            </p>
          )}
        </div>
        <button
          onClick={onDismiss}
          className="text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 shrink-0"
        >
          Dismiss
        </button>
      </div>

      {ordered.length > 0 && (
        <ul className="mt-3 flex flex-col gap-2">
          {ordered.map(([reason, items]) => (
            <li
              key={reason}
              className="text-sm border-l-2 border-amber-400 dark:border-amber-500 pl-3"
            >
              <span className="text-gray-800 dark:text-gray-200">{reason}</span>
              <span className="text-gray-400 dark:text-gray-500">
                {' '}
 - {items.length.toLocaleString()} page{items.length === 1 ? '' : 's'}
              </span>
              <span className="block text-xs text-gray-400 dark:text-gray-500 truncate">
                {items
                  .slice(0, 4)
                  .map((i: MarkReadyBlocked) => i.title || `#${i.page_id}`)
                  .join(', ')}
                {items.length > 4 && ` and ${items.length - 4} more`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
